/** @jsxImportSource @emotion/react */
import { css, SerializedStyles } from "@emotion/react";
import { getResource } from "../../../language/resources/getResource";
import { useCallback, useMemo, useState } from "react";
import { TimeFormat } from "../../../forms/elements/timeInputUtils";
import * as Buttons from "../../../forms/elements/buttons";
import {
  createEmptyEditableBlackoutDateRangeRule,
  EditableBlackoutDateRangeAccessRule,
  findOverlappingBlackoutDateRangeRuleIds,
} from "./blackoutDateRange";
import { BlackoutDateRangeRuleRow } from "./BlackoutDateRangeRuleRow";
import { EditableEffectiveDateRangeAccessRule } from "../effectiveDateRange/effectiveDateRange";
import { accessRuleFieldset } from "../ruleEditorStyles";

type Props = {
  resources: Record<string, any>;
  dateFormat: string;
  timeFormat: TimeFormat;
  rules: EditableBlackoutDateRangeAccessRule[];
  effectiveDateRange?: EditableEffectiveDateRangeAccessRule;
  onChange: (rules: EditableBlackoutDateRangeAccessRule[]) => void;
  submitCount: number;
  hasChangesSinceSubmit: boolean;
  rootCss?: SerializedStyles;
};

export function BlackoutDateRangeAccessRuleEditor({
  resources,
  dateFormat,
  timeFormat,
  rules,
  effectiveDateRange,
  onChange,
  submitCount,
  hasChangesSinceSubmit,
  rootCss,
}: Props) {
  const [localRules, setLocalRules] = useState<
    EditableBlackoutDateRangeAccessRule[]
  >(() => rules);

  const overlappingRuleIds = useMemo(
    () => findOverlappingBlackoutDateRangeRuleIds(localRules),
    [localRules],
  );

  const updateRules = useCallback(
    (
      updater: (
        prev: EditableBlackoutDateRangeAccessRule[],
      ) => EditableBlackoutDateRangeAccessRule[],
    ) => {
      const updated = updater(localRules);

      setLocalRules(updated);
      onChange(updated);
    },
    [localRules, onChange],
  );

  const addNewRule = useCallback(() => {
    updateRules((prev) => [
      ...prev,
      createEmptyEditableBlackoutDateRangeRule(),
    ]);
  }, [updateRules]);

  const handleRuleChange = useCallback(
    (updatedRule?: EditableBlackoutDateRangeAccessRule) => {
      if (!updatedRule) return;

      updateRules((prev) =>
        prev.some((r) => r.localRuleId === updatedRule.localRuleId)
          ? prev.map((r) =>
              r.localRuleId === updatedRule.localRuleId ? updatedRule : r,
            )
          : [...prev, updatedRule],
      );
    },
    [updateRules],
  );

  const handleDeleteRule = useCallback(
    (ruleToDelete: EditableBlackoutDateRangeAccessRule) => {
      updateRules((prev) =>
        prev.filter((r) => r.localRuleId !== ruleToDelete.localRuleId),
      );
    },
    [updateRules],
  );

  return (
    <div css={rootCss}>
      <fieldset css={accessRuleFieldset}>
        <legend>
          {getResource(resources, "ACCESS_RULE_EDIT_BLACKOUT_DATE_RANGE")}
        </legend>
        {localRules.map((rule) => (
          <BlackoutDateRangeRuleRow
            key={rule.localRuleId}
            resources={resources}
            dateFormat={dateFormat}
            timeFormat={timeFormat}
            rule={rule}
            effectiveDateRange={effectiveDateRange}
            hasDateOverlap={overlappingRuleIds.has(rule.localRuleId)}
            onChange={handleRuleChange}
            onDelete={handleDeleteRule}
            submitCount={submitCount}
            hasChangesSinceSubmit={hasChangesSinceSubmit}
          />
        ))}
      </fieldset>

      <Buttons.SmallSecondary
        css={css`
          margin-top: 10px;
        `}
        type="button"
        onClick={addNewRule}
        title={getResource(resources, "ACCESS_RULE_EDIT_ADD_NEW")}
      >
        {getResource(resources, "ACCESS_RULE_EDIT_ADD_NEW")}
      </Buttons.SmallSecondary>
    </div>
  );
}
